/**
 * Career-guide left rail: scroll-with-the-page, then stick (client QA).
 *
 * A geography-scoped guide list can still run taller than the viewport. Plain
 * `position: sticky; top: 24px` then pins the TOP of the rail and leaves the
 * tail of the list permanently below the fold — you can never scroll to it.
 *
 * The client asked for the opposite feel: the rail scrolls up with the page
 * until its BOTTOM reaches the viewport bottom, then sticks there; scrolling
 * back up releases it and reveals the top again. Every link is reachable by
 * ordinary page scrolling — no inner scrollbar, nothing missed because the
 * browser window is too short.
 *
 * The trick is a NEGATIVE sticky top. For a rail taller than the viewport we
 * set `top = viewportH - railH - gap`; sticky then engages only once the rail
 * has scrolled up far enough that its bottom sits `gap` above the viewport
 * bottom. A rail that fits keeps the normal +24px top pin. The offset depends
 * on both the rail height (fonts, list length) and the viewport height, so we
 * recompute on load, after webfonts settle, and on resize.
 *
 * CSS (_career-guide.scss) keeps a viewport-capped hidden-scrollbar box as the
 * no-JS fallback; this controller opts into the flow mode by adding
 * `.cg-nav--in-flow` (drop the cap so the rail takes its full height) and
 * driving the `--cg-nav-top` custom property the sticky `top` reads.
 */
const TOP_GAP = 24;
const BOTTOM_GAP = 24;

export default function initStickyNav() {
	const navs = document.querySelectorAll('.cg-nav');
	if (!navs.length) return;

	const update = (nav) => {
		// The stacked (mobile) layout renders the rail static — no sticky maths,
		// and clear any offset left over from a wider layout.
		if (getComputedStyle(nav).position !== 'sticky') {
			nav.style.removeProperty('--cg-nav-top');
			return;
		}

		const railH = nav.offsetHeight;
		const viewportH = window.innerHeight;
		const top =
			railH + TOP_GAP <= viewportH
				? TOP_GAP
				: viewportH - railH - BOTTOM_GAP;

		nav.style.setProperty('--cg-nav-top', `${Math.round(top)}px`);
	};

	const updateAll = () => navs.forEach(update);

	// Opt out of the capped-box fallback so the rail can take its full height
	// (measured below), then compute the sticky offset.
	navs.forEach((nav) => nav.classList.add('cg-nav--in-flow'));
	updateAll();

	// The hosted webfont kits can land after document.fonts.ready resolves and
	// re-flow the list taller — recompute now, after fonts, after load, and
	// once more shortly after, taking whichever layout is current.
	document.fonts?.ready?.then(updateAll);
	window.addEventListener('load', () => {
		updateAll();
		setTimeout(updateAll, 600);
	});

	let timer;
	window.addEventListener('resize', () => {
		clearTimeout(timer);
		timer = setTimeout(updateAll, 150);
	});
}
